"use client";

import { useMemo } from "react";
import type { CalendarFestival } from "@/lib/api/calendar";
import { getDaysInMonth, getFirstDayOfWeek, MONTH_NAMES, TYPE_COLORS } from "./types";

interface CalendarGridProps {
  year: number;
  month: number;
  festivals: CalendarFestival[];
  onSelect: (date: string) => void;
  /** The observance day whose details are open below the grid. */
  selectedDate?: string | null;
  /**
   * Today in India (YYYY-MM-DD), from the server. The grid renders on the
   * server too, so it never reads the browser clock: a visitor abroad, or a
   * render near midnight, would otherwise highlight a different day than the
   * HTML did. Null highlights no day.
   */
  today?: string | null;
}

/**
 * Only a day with an observance is a control (it opens that day's details);
 * every other day is a plain cell. They were all button elements that did nothing
 * (b-content-08: 14 dead clicks from 7 of 45 users in 28 days). With no data
 * (loading) the grid still renders every week row, so it holds its height.
 */
export function CalendarGrid({
  year,
  month,
  festivals,
  onSelect,
  selectedDate = null,
  today = null,
}: CalendarGridProps) {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);

  // Build date → festivals map
  const festivalMap = useMemo(() => {
    const map: Record<string, CalendarFestival[]> = {};
    for (const f of festivals) {
      const day = f.date;
      if (!map[day]) map[day] = [];
      map[day].push(f);
    }
    return map;
  }, [festivals]);

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="w-full">
      {/* Day headers */}
      <div className="grid grid-cols-7 gap-px mb-1">
        {dayLabels.map((d) => (
          <div
            key={d}
            className="text-center text-[10px] font-bold text-ish-text-muted py-1 uppercase tracking-widest"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, idx) => {
          if (day === null) {
            return <div key={`empty-${idx}`} className="aspect-square" aria-hidden="true" />;
          }
          const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const dayFestivals = festivalMap[dateStr] || [];
          const isToday = dateStr === today;

          const hasFestival = dayFestivals.some((f) => f.type === "festival");
          const hasVrat = dayFestivals.some((f) => f.type === "vrat");
          const hasEclipse = dayFestivals.some((f) => f.type === "eclipse");
          // Purnima, Amavasya, Sankranti and the other monthly tithi rows.
          const hasTithi = dayFestivals.some(
            (f) => f.type !== "festival" && f.type !== "vrat" && f.type !== "eclipse"
          );

          const hasEvents = dayFestivals.length > 0;
          const isSelected = hasEvents && dateStr === selectedDate;
          const cellClass = `relative aspect-square flex flex-col items-center justify-center rounded-xl transition-all
                ${isToday ? "border-2 border-ish-accent bg-ish-accent/5" : "border border-transparent bg-black/[0.02]"}
                ${isSelected ? "ring-2 ring-intent-brand" : ""}`;
          const content = (
            <>
              <span
                className={`text-sm ${isToday ? "font-bold text-intent-brand" : "text-ish-dark font-medium"}`}
              >
                {day}
              </span>

              {/* Dots */}
              {hasEvents && (
                <span
                  aria-hidden="true"
                  className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-0.5"
                >
                  {hasFestival && (
                    <span className={`w-1 h-1 rounded-full ${TYPE_COLORS.festival.dot}`} />
                  )}
                  {hasVrat && <span className={`w-1 h-1 rounded-full ${TYPE_COLORS.vrat.dot}`} />}
                  {hasEclipse && (
                    <span className={`w-1 h-1 rounded-full ${TYPE_COLORS.eclipse.dot}`} />
                  )}
                  {hasTithi && (
                    <span className={`w-1 h-1 rounded-full ${TYPE_COLORS.tithi_based.dot}`} />
                  )}
                </span>
              )}
            </>
          );

          if (!hasEvents) {
            return (
              <div key={dateStr} data-calendar-cell className={cellClass}>
                {content}
              </div>
            );
          }
          return (
            <button
              type="button"
              key={dateStr}
              data-calendar-cell
              onClick={() => onSelect(dateStr)}
              aria-pressed={isSelected}
              aria-label={`${day} ${MONTH_NAMES[month - 1]}: ${dayFestivals
                .map((festival) => festival.name_en)
                .join(", ")}`}
              // 39px at 360px wide. The ::after sits on the padding box (inside the
              // border), so a 5px inset reaches 4px past the visible edge, the
              // gap-1 gutter: a ~46.5px tap area that leaves the grid as is.
              className={`${cellClass} cursor-pointer hover:bg-ish-accent/5 after:absolute after:-inset-1.25`}
            >
              {content}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 justify-center mt-6 text-[11px] font-medium text-ish-text-muted">
        {[
          { label: "Festival", color: TYPE_COLORS.festival.dot },
          { label: "Vrat", color: TYPE_COLORS.vrat.dot },
          { label: "Eclipse", color: TYPE_COLORS.eclipse.dot },
          { label: "Tithi", color: TYPE_COLORS.tithi_based.dot },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${item.color}`} />
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
}
